import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SalesNoteList, SalesNoteLine } from "interface/salesNote";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";
import { formatAmount } from "utils/formatters";
import { formatWeight } from "utils/formatters";

interface SalesNotesMobileListProps {
	items: SalesNoteList[];
	loading: boolean;
	onLoadMore: () => void;
}

const getStatusClassName = (status: number, signStatus?: number) => {
	if (status === 1) return "sales-note-list-card--status-red";
	if (status === 2) return "sales-note-list-card--status-yellow";
	if (status === 4 && signStatus === 0)
		return "sales-note-list-card--status-orange";
	return "sales-note-list-card--status-green";
};

const SalesNoteListMobile: FC<SalesNotesMobileListProps> = ({
	items,
	loading,
	onLoadMore,
}) => {
	const { t } = useTranslation();

	const empty = useMemo(
		() => !loading && items.length === 0,
		[items.length, loading],
	);

	return (
		<div className="sales-note-list-mobile">
			{empty && (
				<div className="sales-note-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const itemKey = `${item.noteNumber}-${item.source ?? "nosource"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`sales-note-list-card ${getStatusClassName(item.noteStatusId, item.signStatus)}`}
					>
						<div className="sales-note-list-card-header">
							<div className="sales-note-list-card-note">{item.noteNumber}</div>
							<div className="sales-note-list-card-date">
								<GrideDateFormatter
									date={item.landingTime}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							</div>
						</div>

						<div className="sales-note-list-card-body">
							<div>
								<span>{t("VesselRegistrationNo")}</span>
								<strong>{item.vesselRegistrationId || "-"}</strong>
							</div>
							<div>
								<span>{t("Vessel")}</span>
								<strong>{item.vesselName || "-"}</strong>
							</div>
							<div>
								<span>{t("RegistrationDateShort")}</span>
								<strong>
									<GrideDateFormatter
										date={item.registrationTime}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								</strong>
							</div>
							<div>
								<span>{t("LastCatchDate")}</span>
								{item.lastCatchDate ? (
									<strong>
										<GrideDateFormatter
											date={item.lastCatchDate || ""}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									</strong>
								) : (
									<strong>{"-"}</strong>
								)}
							</div>
							<div>
								<span>{"Melding"}</span>
								<strong>{item.registrationId || "-"}</strong>
							</div>

							<div>
								<span>{t("CatchArea")}</span>
								<strong>{item.catchArea || "-"}</strong>
							</div>
							<div>
								<span>{t("FishingGear")}</span>
								<strong>{item.fishingGear || "-"}</strong>
							</div>
							<div>
								<span>{t("TotalWeight")}</span>
								<strong>{formatWeight(item.netLiveWeightKg)}</strong>
							</div>
							<div>
								<span>{t("RoundWeight")}</span>
								<strong>{formatWeight(item.roundWeightKg)}</strong>
							</div>
							<div>
								<span>{t("TotalValue")}</span>
								<strong>{formatAmount(item.amount)}</strong>
							</div>
						</div>

						<div className="sales-note-list-card-actions"></div>
					</div>
				);
			})}

			{loading && (
				<div className="sales-note-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default SalesNoteListMobile;
